import React, { useCallback, useMemo, useState } from 'react';
import { Button, Chip, Divider, Fab, Paper, Stack, Typography } from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { addDays, dayLabel, today } from '../labels';
import { CardGrid, EmptyState, Loader, MemberAvatar, NeedsHousehold, PageHeader, Panel, useHousehold, useLoad } from '../common';
import { TaskRow, useTaskActions } from './taskActions';
import { replaceOccurrence } from './agenda';

const DAYS_AHEAD = 6;
const ALL = 'all';

interface Section { title: string; items: T.TaskOccurrence[]; }

/** Overdue tasks and the next seven days, grouped by day. */
const TasksPage: React.FC = () => {
  const { members } = useHousehold();
  const navigate = useNavigate();
  const [person, setPerson] = useState<string>(ALL);
  const now = today();
  const { data: agenda, setData: setAgenda, error, loading, reload } = useLoad(() => api.getTaskAgenda(now, addDays(now, DAYS_AHEAD)), [now]);
  // The row changes with the server's answer: no reload of the agenda after a change made here
  const replace = useCallback((updated: T.TaskOccurrence) => setAgenda(current => current && replaceOccurrence(current, updated)), [setAgenda]);
  const actions = useTaskActions(members, reload, replace);

  const sections = useMemo<Section[]>(() => {
    if (!agenda) return [];
    const visible = (o: T.TaskOccurrence) => person === ALL || o.assigneePublicId === person || !o.assigneePublicId;
    const result: Section[] = [];
    const overdue = agenda.overdue.filter(visible);
    if (overdue.length > 0) result.push({ title: 'Em atraso', items: overdue });

    const upcoming = agenda.occurrences.filter(visible).filter(o => !o.isOverdue);
    const flexible = upcoming.filter(o => o.windowEnd && o.status === 'Pending');
    for (let i = 0; i <= DAYS_AHEAD; i++) {
      const day = addDays(now, i);
      const items = upcoming.filter(o => o.date === day && flexible.indexOf(o) < 0);
      if (items.length === 0) continue;
      result.push({ title: i === 0 ? 'Hoje' : i === 1 ? 'Amanhã' : dayLabel(day), items });
    }
    if (flexible.length > 0) result.push({ title: 'Esta semana, sem dia fixo', items: flexible });
    return result;
  }, [agenda, person, now]);

  return (
    <>
      <PageHeader
        eyebrow="Semana"
        title="Tarefas"
        subtitle="Em atraso e os próximos sete dias"
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/casa/tarefas/nova')} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>Nova tarefa</Button>}
      />
      {members.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }} role="group" aria-label="Pessoa">
          <Chip label="Todos" onClick={() => setPerson(ALL)} color={person === ALL ? 'primary' : 'default'} variant={person === ALL ? 'filled' : 'outlined'} />
          {members.map(m => (
            <Chip
              key={m.publicId}
              avatar={<MemberAvatar member={m} size={24} />}
              label={m.name.split(' ')[0]}
              onClick={() => setPerson(m.publicId)}
              color={person === m.publicId ? 'primary' : 'default'}
              variant={person === m.publicId ? 'filled' : 'outlined'}
              aria-pressed={person === m.publicId}
            />
          ))}
        </Stack>
      )}
      <Loader loading={loading} error={error} onRetry={reload} hasData={agenda !== undefined}>
        {sections.length === 0 ? (
          <Paper variant="outlined" sx={{ mt: 2 }}>
            <EmptyState
              icon={<TaskAltIcon />}
              title="Sem tarefas para os próximos dias"
              text="Cria uma com «Nova tarefa». As tarefas da ementa (descongelar, demolhar) e das compras aparecem sozinhas."
              action={<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>«pôr o lixo lá fora às terças e sextas»</Typography>}
            />
          </Paper>
        ) : (
          <CardGrid min={340} gap={2} sx={{ mt: 1 }}>
            {sections.map(section => (
              <Panel
                key={section.title}
                title={section.title}
                tone={section.title === 'Em atraso' ? 'warning' : undefined}
                action={<Typography variant="caption" color="text.secondary">{section.items.length}</Typography>}
                sx={{ pb: 0.5 }}
              >
                {section.items.map((o, i) => (
                  <React.Fragment key={o.publicId}>
                    {i > 0 && <Divider component="div" sx={{ mx: 2 }} />}
                    <TaskRow occurrence={o} members={members} actions={actions} />
                  </React.Fragment>
                ))}
              </Panel>
            ))}
          </CardGrid>
        )}
      </Loader>
      <Fab
        color="primary" aria-label="Nova tarefa" onClick={() => navigate('/casa/tarefas/nova')}
        sx={{ position: 'fixed', right: 16, bottom: 80, display: { xs: 'flex', md: 'none' } }}
      >
        <AddIcon />
      </Fab>
      {actions.element}
    </>
  );
};

const TasksPageRoute: React.FC = () => <NeedsHousehold><TasksPage /></NeedsHousehold>;
export default TasksPageRoute;
